import api from "./axiosConfig";

const handleError = (type, error) => {
  console.error(`${type} request failed:`, error);
  throw error;
};

/**
 * Generic function to make a GET request with axios.
 * @param {string} url - The URL of the API endpoint.
 * @param {Object} [params={}] - URL parameters for GET requests.
 * @param {Object} [headers={}] - Custom headers to include in the request.
 * @returns {Promise<Object>} The response data from the API call.
 */
export const getRequest = async (url, params = {}, headers = {}) => {
  try {
    const response = await api.get(url, { params, headers });
    return response.data;
  } catch (error) {
    handleError("GET", error);
  }
};

/**
 * Campaign-insights fetch, chunked by campaign id.
 *
 * The backend already splits its upstream TikTok/Meta calls at 100 IDs, but
 * the browser→API URL has its own ceiling: joining every campaign id into one
 * query string produces a multi-KB URL that nginx rejects with 414 before
 * Express ever runs (~470 TikTok campaigns ≈ 9 KB). Chunk at the same 100-ID
 * cap and merge the per-chunk {campaign_id: metrics} maps.
 *
 * @param {"tiktok"|"meta"} platform - Insights endpoint to hit.
 * @param {string[]} campaignIds - Campaign ids to fetch metrics for.
 * @param {string} startDate - YYYY-MM-DD.
 * @param {string} endDate - YYYY-MM-DD.
 * @returns {Promise<Object>} Merged {campaign_id: metrics} map.
 */
const INSIGHTS_IDS_PER_REQUEST = 100;

export const getCampaignInsightsChunked = async (platform, campaignIds, startDate, endDate) => {
  const chunks = [];
  for (let i = 0; i < campaignIds.length; i += INSIGHTS_IDS_PER_REQUEST) {
    chunks.push(campaignIds.slice(i, i + INSIGHTS_IDS_PER_REQUEST));
  }
  const results = await Promise.all(
    chunks.map((chunk) =>
      getRequest(
        `/api/ads/${platform}/campaign-insights?campaign_ids=${chunk.join(",")}&start_date=${startDate}&end_date=${endDate}`
      )
    )
  );
  return Object.assign({}, ...results.map((r) => r?.data || {}));
};

/**
 * Generic function to make a POST request with axios.
 * @param {string} url - The URL of the API endpoint.
 * @param {Object} [data={}] - The data to send in the request body (for POST requests).
 * @param {Object} [headers={}] - Custom headers to include in the request.
 * @returns {Promise<Object>} The response data from the API call.
 */
export const postRequest = async (url, data = {}, headers = {}) => {
  try {
    const response = await api.post(url, data, { headers });
    return response.data;
  } catch (error) {
    handleError("POST", error);
  }
};

/**
 * Generic function to make a PATCH request with axios.
 * @param {string} url - The URL of the API endpoint.
 * @param {Object} [data={}] - The data to send in the request body (for PATCH requests).
 * @param {Object} [headers={}] - Custom headers to include in the request.
 * @returns {Promise<Object>} The response data from the API call.
 */
export const patchRequest = async (url, data = {}, headers = {}) => {
  try {
    const response = await api.patch(url, data, { headers });
    return response.data;
  } catch (error) {
    handleError("PATCH", error);
  }
};

/**
 * Generic function to make a DELETE request with axios.
 * @param {string} url - The URL of the API endpoint.
 * @param {Object} [headers={}] - Custom headers to include in the request.
 * @returns {Promise<Object>} The response data from the API call.
 */
export const deleteRequest = async (url, headers = {}) => {
  try {
    const response = await api.delete(url, { headers });
    return response.data;
  } catch (error) {
    handleError("DELETE", error);
  }
};
